// Mapa de flota del supervisor — marca cada chofer en su última posición
// GPS con un ícono de camión + label con código y apellido. Click en un
// marker dispara `onSelect(driverId)`. Los choferes sin GPS no se
// renderizan (el supervisor ya los ve en la lista igual).
//
// Reusa el patrón de ProgramacionMap (react-leaflet + DivIcon HTML) en
// vez de markercluster — acá la lectura agrupada no aporta porque
// queremos identificar a cada chofer por nombre.

import { useEffect, useMemo } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { driverPcts, formatPct } from "./fleetStats";
import type { LiveVehicle } from "./useLiveFleet";

const LIMA_CENTER: [number, number] = [-12.0464, -77.0428];

type Props = {
  vehicles: LiveVehicle[];
  selectedId?: string;
  onSelect: (id: string) => void;
};

export function SupervisorFleetMap({ vehicles, selectedId, onSelect }: Props) {
  // GPS del chofer DESACTIVADO. Resolvemos la ubicación con prioridad:
  //   1) Último pedido atendido (POD del último delivered/failed) —
  //      la información operativa más útil: "hasta dónde llegó".
  //   2) Almacén — para los que no operaron todavía; al menos se
  //      ubican en su punto de origen, con ícono dashed gris.
  //   3) Sin coords → fuera del mapa.
  const located = useMemo(
    () =>
      vehicles
        .map((d) => {
          if (d.lastStopLat != null && d.lastStopLon != null) {
            return {
              vehicle: d,
              lat: d.lastStopLat,
              lon: d.lastStopLon,
              source: "lastStop" as const,
            };
          }
          if (d.warehouseLat != null && d.warehouseLon != null) {
            return {
              vehicle: d,
              lat: d.warehouseLat,
              lon: d.warehouseLon,
              source: "warehouse" as const,
            };
          }
          return null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
    [vehicles],
  );

  const initialCenter = useMemo<[number, number]>(() => {
    if (located.length === 0) return LIMA_CENTER;
    let lat = 0, lon = 0;
    for (const d of located) {
      lat += d.lat;
      lon += d.lon;
    }
    return [lat / located.length, lon / located.length];
  }, [located]);

  return (
    <MapContainer
      center={initialCenter}
      zoom={12}
      minZoom={5}
      maxZoom={22}
      scrollWheelZoom
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={22}
        maxNativeZoom={19}
      />
      <FitOnFirstLoad points={located.map((d) => [d.lat, d.lon])} />
      {located.map(({ vehicle, lat, lon, source }) => (
        <Marker
          key={vehicle.id}
          position={[lat, lon]}
          icon={driverIcon(vehicle, vehicle.id === selectedId, source)}
          eventHandlers={{ click: () => onSelect(vehicle.id) }}
        >
          <Popup>
            <DriverPopupContent d={vehicle} source={source} />
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

/// Origen de la posición del marker.
///   - "lastStop"  → ubicación del último pedido atendido del día
///                   (entrega real o rechazo). Fuente principal.
///   - "warehouse" → fallback al almacén (vehículo sin operación);
///                   ícono con borde dashed.
type MarkerSource = "lastStop" | "warehouse";

// Tarjeta dentro del popup del marker. La info clave del chofer "junto"
// a su posición — el supervisor no tiene que mover los ojos al panel
// inferior. Incluye: identificación, % Ef.Visita/Entrega, contadores y
// hora de último GPS.
function DriverPopupContent({ d, source }: { d: LiveVehicle; source: MarkerSource }) {
  const { efVisita, efEntrega } = driverPcts(d);
  return (
    <div style={{ minWidth: 200, fontFamily: "inherit" }}>
      <div style={{ fontWeight: 600, color: "#1565C0", marginBottom: 2 }}>
        {d.plate}
        {d.driverFullName ? ` · ${d.driverFullName}` : ""}
      </div>
      <div style={{ fontSize: 11, color: "#5C6B7A", marginBottom: 8 }}>
        {d.driverFullName ? "Vehículo + chofer" : "Vehículo (sin chofer asignado)"} ·
        {source === "warehouse" ? (
          <span style={{ color: "#C97400", fontWeight: 600 }}>
            {" "}sin operación aún — mostrado en su almacén
          </span>
        ) : (
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>
            {" "}último pedido atendido{d.lastStopAt ? ` a las ${d.lastStopAt.toLocaleTimeString("es-PE", { hour12: false })}` : ""}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
        <Stat label="Ef. Visita"  value={formatPct(efVisita)} />
        <Stat label="Ef. Entrega" value={formatPct(efEntrega)} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, paddingTop: 6, borderTop: "1px solid #D9E2EC" }}>
        <Stat label="Total"      value={String(d.stopsTotal)} />
        <Stat label="Efectivas"  value={String(d.stopsDone)} />
        <Stat label="Rechazos"   value={String(d.stopsFailed)} danger={d.stopsFailed > 0} />
      </div>
    </div>
  );
}

function Stat({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: danger ? "#C62828" : "#243B53" }}>
        {value}
      </div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "#5C6B7A" }}>
        {label}
      </div>
    </div>
  );
}

// ---------- Icono ----------
//
// DivIcon con un círculo coloreado según estado + label flotante con
// código y apellido. El color del fondo:
//   - rojo  → tiene alerta (sin GPS reciente o atrasado)
//   - verde → entregando o sin alertas
// El borde se vuelve naranja cuando el chofer está seleccionado.
function driverIcon(
  d: LiveVehicle,
  selected: boolean,
  source: MarkerSource,
): L.DivIcon {
  // Color según fuente de la posición:
  //   - lastStop → verde (operando, hay entregas reales)
  //   - warehouse → gris dashed (sin operación, en su almacén)
  const fillColor = source === "warehouse" ? "#90A4AE" : "#2E7D32";
  const borderColor = selected ? "#FF8F00" : "#FFFFFF";
  const borderStyle = source === "warehouse" ? "dashed" : "solid";
  // El label del pin es la placa (identificador primario en la operación);
  // si hay chofer asignado le sumamos el apellido como pista visual.
  const label = `${d.plate}${d.driverFullName ? " " + lastName(d.driverFullName) : ""}`;
  // El HTML está controlado y no toma input del usuario que no sea ya
  // texto del staff — XSS no aplica pero igual escapamos por reflejo.
  const safe = escapeHtml(label);
  // Camión detallado (caja + cabina + ruedas) — silueta blanca sobre
  // el círculo coloreado. Consistente con el ícono usado en
  // TrackingPage para que el supervisor reconozca el mismo símbolo.
  const truck = `
    <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="2" y="7" width="12" height="9" rx="0.8"/>
      <path d="M14 9 L20.2 9 Q21 9 21.4 9.6 L22.6 11.4 Q23 12 23 12.6 L23 16 Q23 16.6 22.4 16.6 L14 16.6 Z"/>
      <rect x="15.5" y="10.2" width="3.8" height="3" fill="${fillColor}"/>
      <circle cx="7" cy="17.5" r="2.2" fill="#222"/>
      <circle cx="7" cy="17.5" r="0.9" fill="white"/>
      <circle cx="18.5" cy="17.5" r="2.2" fill="#222"/>
      <circle cx="18.5" cy="17.5" r="0.9" fill="white"/>
    </svg>
  `;
  const html = `
    <div class="micelio-driver-pin">
      <div class="pin" style="background:${fillColor};border-color:${borderColor};border-style:${borderStyle}">
        <div style="width:20px;height:20px;">${truck}</div>
      </div>
      <div class="label">${safe}</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: "", // evitamos default styles
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

// Encuadra el mapa a todos los puntos del primer mount.
function FitOnFirstLoad({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// ---------- helpers ----------


// "RAMOS MEDRANO RANDY JUNIOR" → "RAMOS" (primer token). Mantiene el
// label corto para que no tape el mapa cuando hay muchos pines.
function lastName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? "";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
